import { createContext, useEffect, useReducer } from 'react';
import PropTypes, { bool } from 'prop-types';
import AuthRepository from '../repositories/auth-repository';
import AxiosService from '../services/axios-service';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  token: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated: isAuthenticated,
      isInitialized: true,
      user: user
    };
  },
  CODE: (state, action) => {
    const { token, user } = action.payload;
    return {
      ...state,
      token: token,
      user: user
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user: user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user: user
    };
  }
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  setToken: (token, user) => {},
  login: async (cpf) => bool,
  loginSeller: async (cpf) => bool,
  logout: async () => {},
  register: async (user) => {},
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const user = await AuthRepository.getUserData();
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: user
          }
        });
      } catch (error) {
        console.error(error);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const setToken = (token, user) => {
    dispatch({
      type: 'CODE',
      payload: {
        token: token,
        user: user
      }
    });
  }

  const login = async (cpf, verificationProof) => {
    try {
      await AuthRepository.login(cpf, verificationProof);
      const user = await AuthRepository.getUserData();
      dispatch({
        type: 'LOGIN',
        payload: {
          user: user
        }
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null
        }
      });
      throw error;
    }
  };

  const loginSeller = async (cpf) => {
    try {
      await AuthRepository.loginSeller(cpf);
      const user = await AuthRepository.getUserData();
      dispatch({
        type: 'LOGIN',
        payload: {
          user: user
        }
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null
        }
      });
      throw error;
    }
  };

  const register = async (user) => {
    const createdUser = {};
    dispatch({
      type: 'REGISTER',
      payload: {
        user: createdUser
      }
    });
  };

  const logout = async () => {
    const axiosService = new AxiosService();
    axiosService.deleteTokens();
    dispatch({ type: 'LOGOUT' });
  };

  const resetPassword = () => { };

  const updateProfile = () => { };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        setToken,
        login,
        loginSeller,
        logout,
        register,
        resetPassword,
        updateProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
